import React, { FunctionComponent, useEffect, useState } from 'react';

import classNames from 'classnames';
import { isEqual } from 'lodash';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { VoBScoring } from 'common/response-editor/vob/VobScoring';
import { isVobEnabled } from 'common/response-editor/vob/VobUtils';
import { Overlay } from 'core/overlay/Overlay';
import { usePrevious } from 'core/util/Hook';
import { useReplies, RepliesActionType } from 'reviews2/feed/review-yeti//Replies.store';
import { ReplyActionsContainer } from 'reviews2/feed/review-yeti/reply/actions-container/ReplyActionsContainer';
import { ReplyContent } from 'reviews2/feed/review-yeti/reply/content/ReplyContent';
import { ReplyCustomActions } from 'reviews2/feed/review-yeti/reply/custom-actions/ReplyCustomActions';
import { ReplyDate } from 'reviews2/feed/review-yeti/reply/date/ReplyDate';
import { ReplyDirectMessage } from 'reviews2/feed/review-yeti/reply/direct-message/ReplyDirectMessage';
import { ReplyEditor } from 'reviews2/feed/review-yeti/reply/editor/ReplyEditor';
import { ReplyFailedReason } from 'reviews2/feed/review-yeti/reply/failed/ReplyFailedReason';
import { ReplyLabels } from 'reviews2/feed/review-yeti/reply/labels/ReplyLabels';
import { ReplyModeration } from 'reviews2/feed/review-yeti/reply/moderation/ReplyModeration';
import { ReplyPoster } from 'reviews2/feed/review-yeti/reply/poster/ReplyPoster';
import { ReplyProps } from 'reviews2/feed/review-yeti/reply/ReviewReply.types';
import { ReplyStatus } from 'reviews2/feed/review-yeti/reply/status/ReplyStatus';

import 'reviews2/feed/review-yeti/reply/ReviewReply.scss';

export const ReviewReply: FunctionComponent<ReplyProps> = ({ context, reply }) => {
    const { t } = useTranslation('reviews2');
    const { options } = context;
    const [{ currentReply }, dispatch] = useReplies();
    const [showReplies, setShowReplies] = useState<boolean>(false);
    const [count, setCount] = useState(reply.replies?.length);
    const supportNested = false;
    const isHidden = reply.properties?.isHidden?.booleanValues?.[0];
    const previousRepliesCount = usePrevious(reply.replies?.length);

    useEffect(() => {
        setCount(reply.replies?.length);
    }, [reply.replies, previousRepliesCount]);

    const setReply = () => {
        if (isEqual(reply.id, currentReply?.id)) {
            return;
        }

        dispatch({
            type: RepliesActionType.SetReply,
            reply,
        });
    };

    const { review, profile } = context;

    const vobEnabled: boolean = isVobEnabled(review.tenant, profile);

    return (
        <div
            role="button"
            tabIndex={0}
            id={reply.document_id}
            onClick={setReply}
            onKeyPress={setReply}
            className="r4e-review-reply"
        >
            <div className="r4e-review-reply-container">
                <div
                    className={classNames('r4e-review-reply-content', {
                        active: isEqual(reply.document_id, currentReply?.document_id),
                        hidden: isHidden,
                    })}
                >
                    <div className="content-group">
                        <ReplyPoster context={context} reply={reply} />
                        <ReplyLabels context={context} reply={reply} />
                        {vobEnabled ? (
                            <div className="vob-scoring">
                                <VoBScoring meetsThreshold={reply.vob_threshold_met} isInsideEditor={false} />
                            </div>
                        ) : null}
                        <ReplyActionsContainer context={context} reply={reply} />
                    </div>
                    <ReplyContent context={context} reply={reply} />
                    <ReplyStatus context={context} reply={reply} />
                    <ReplyModeration context={context} reply={reply} vobReviewResponseEnabled={vobEnabled} />
                    <ReplyDirectMessage context={context} reply={reply} />
                    <ReplyFailedReason context={context} reply={reply} />
                    <ReplyCustomActions context={context} reply={reply} />
                </div>
                <div className="r4e-review-reply-actions">
                    <ReplyDate context={context} reply={reply} />
                </div>

                {showReplies && reply.replies && <div className="r4e-review-reply-replies" />}
                {showReplies && options?.showEditor && (
                    <span className="notranslate">
                        <ReplyEditor context={context} />
                    </span>
                )}
                {count! > 0 && supportNested && (
                    <div className="r4e-reply-replies-show">
                        <Overlay area="reply-replies" />
                        <Button variant="link" onClick={(): void => setShowReplies(!showReplies)}>
                            <i className={`icon-arrow-${showReplies ? 'left' : 'right'}`} />
                            <span className="text">
                                {t(showReplies ? 'HIDE_REPLIES' : 'REPLY_MORE_REPLIES', {
                                    count,
                                })}
                            </span>
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
};
